import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Skeleton, SkeletonAvatar, SkeletonBadge, SkeletonText } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * The cell shapes a column can stand in for. Picking the right one is what
 * makes a table skeleton read as *this* table rather than as a grey grid.
 */
type SkeletonCellVariant =
    /** One line of `text-sm`. */
    | 'text'
    /** Name over a caption — the two-line first column most lists use. */
    | 'twoLine'
    /** Avatar beside a two-line name block. */
    | 'avatarText'
    /** A status pill. */
    | 'badge'
    /** Right-aligned figure, narrow. */
    | 'number'
    /** A short row of inline glyphs, e.g. the channel icons on sequences. */
    | 'icons'
    /** Trailing row-actions button. */
    | 'actions'
    /** Selection checkbox. */
    | 'checkbox';

interface SkeletonColumn {
    /** Fixed px width, mirroring the real `ColumnDef.size`. Omit for the flex column. */
    readonly size?: number;
    readonly variant?: SkeletonCellVariant;
    /** Header-label width in px. */
    readonly headerWidth?: number;
}

interface DataTableSkeletonProps {
    /**
     * The columns being stood in for.
     *
     * Prefer the array form and pass the same `size` values as the real
     * `ColumnDef`s — that is what makes header and body line up with the loaded
     * table. The numeric form ("just give me N columns") is kept for the many
     * existing call sites and synthesises a plausible shape.
     */
    readonly columns?: number | readonly SkeletonColumn[];
    /**
     * How many body rows to draw. Set this to the query's `limit` — a skeleton
     * that renders fewer rows than the page will hold is the single largest
     * source of layout shift on a list screen.
     */
    readonly rows?: number;
    readonly showToolbar?: boolean;
    readonly showPagination?: boolean;
    readonly toolbarActions?: number;
    readonly className?: string;
}

/** Staggered widths so a column doesn't read as a solid block. */
const NAME_WIDTHS = ['72%', '58%', '84%', '64%', '78%', '52%', '88%', '68%'] as const;
const CAPTION_WIDTHS = ['38%', '46%', '30%', '42%', '34%', '50%', '36%', '44%'] as const;
const HEADER_WIDTHS = [44, 56, 48, 62, 40, 58, 52, 46] as const;
const TOOLBAR_ACTION_WIDTHS = [110, 90, 100] as const;

/**
 * Builds a plausible column spec for the numeric API: a two-line name, a status
 * pill, figures through the middle, and a trailing actions cell.
 */
function synthesiseColumns(count: number): readonly SkeletonColumn[] {
    return Array.from({ length: count }, (_, i): SkeletonColumn => {
        const headerWidth = HEADER_WIDTHS[i % HEADER_WIDTHS.length];
        if (i === 0) return { variant: 'twoLine', headerWidth };
        if (i === count - 1 && count >= 3) return { size: 48, variant: 'actions' };
        if (i === 1) return { size: 110, variant: 'badge', headerWidth };
        return { size: 100, variant: i % 2 === 0 ? 'number' : 'text', headerWidth };
    });
}

function CellContent({
    variant,
    index,
}: {
    readonly variant: SkeletonCellVariant;
    readonly index: number;
}) {
    const nameWidth = NAME_WIDTHS[index % NAME_WIDTHS.length];
    const captionWidth = CAPTION_WIDTHS[index % CAPTION_WIDTHS.length];

    switch (variant) {
        case 'checkbox':
            return <Skeleton className="size-4 rounded-[3px]" />;
        case 'twoLine':
            return (
                <div className="flex min-w-0 flex-col">
                    <SkeletonText size="sm" width={nameWidth} />
                    <SkeletonText size="xs" width={captionWidth} />
                </div>
            );
        case 'avatarText':
            return (
                <div className="flex min-w-0 items-center gap-3">
                    <SkeletonAvatar size={32} />
                    <div className="flex min-w-0 flex-1 flex-col">
                        <SkeletonText size="sm" width={nameWidth} />
                        <SkeletonText size="xs" width={captionWidth} />
                    </div>
                </div>
            );
        case 'badge':
            return <SkeletonBadge width={index % 3 === 0 ? 78 : 64} />;
        case 'number':
            // A figure occupies a `text-sm` line box but only a couple of digits.
            return <SkeletonText size="sm" width="32px" />;
        case 'icons':
            return (
                <div className="flex items-center gap-1.5">
                    <Skeleton className="size-4 rounded-sm" />
                    {index % 2 === 0 && <Skeleton className="size-4 rounded-sm" />}
                </div>
            );
        case 'actions':
            return <Skeleton className="ml-auto size-7 rounded-md" />;
        case 'text':
        default:
            return <SkeletonText size="sm" width={captionWidth} />;
    }
}

/** Fixed-width cells need the width echoed on both the `th` and every `td`. */
function sizeStyle(size: number | undefined): React.CSSProperties | undefined {
    if (size === undefined) return undefined;
    return { width: `${size}px`, minWidth: `${size}px`, maxWidth: `${size}px` };
}

/**
 * Loading placeholder for a `DataTable`.
 *
 * Built from the real `Table` primitives rather than a CSS grid, so header
 * height (`h-10`), cell padding (`px-4 py-3`), borders and the table-header
 * band come from the same source as the loaded table. Row heights therefore
 * match by construction: a `twoLine` column produces the same 60px row the real
 * two-line name cell does.
 *
 * Wrap it in the same bordered container the table uses:
 * `<div className="overflow-hidden rounded-lg border border-border-default">`.
 */
export function DataTableSkeleton({
    columns = 6,
    rows = 10,
    showToolbar = false,
    showPagination = true,
    toolbarActions = 2,
    className,
}: DataTableSkeletonProps) {
    const spec = typeof columns === 'number' ? synthesiseColumns(columns) : columns;
    // `table-fixed` only honours explicit widths; without any, let the browser
    // distribute columns so a spec-less skeleton doesn't collapse to equal thirds.
    const hasFixedWidths = spec.some((column) => column.size !== undefined);

    return (
        <div className={cn('flex flex-1 flex-col', className)}>
            {showToolbar && (
                <div className="flex items-center justify-between border-b border-border-default px-4 py-3">
                    <Skeleton className="h-8 w-60 rounded-md" />
                    <div className="flex gap-2">
                        {Array.from({ length: toolbarActions }).map((_, i) => (
                            <Skeleton
                                key={i}
                                className="h-8 rounded-md"
                                style={{ width: TOOLBAR_ACTION_WIDTHS[i % 3] }}
                            />
                        ))}
                    </div>
                </div>
            )}

            <Table
                className={cn(
                    'border-collapse border-spacing-0 bg-bg-surface',
                    hasFixedWidths && 'table-fixed',
                )}
            >
                <TableHeader>
                    <TableRow>
                        {spec.map((column, i) => (
                            <TableHead key={i} style={sizeStyle(column.size)}>
                                {column.headerWidth !== undefined && (
                                    <Skeleton
                                        className="h-2 rounded-sm"
                                        style={{ width: column.headerWidth }}
                                    />
                                )}
                            </TableHead>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {Array.from({ length: rows }).map((_, rowIndex) => (
                        <TableRow key={rowIndex}>
                            {spec.map((column, colIndex) => (
                                <TableCell key={colIndex} style={sizeStyle(column.size)}>
                                    <CellContent
                                        variant={column.variant ?? 'text'}
                                        index={rowIndex + colIndex}
                                    />
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {showPagination && (
                // Mirrors the real pagination bar's own chrome.
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border-default px-4 py-3">
                    <div className="flex items-center gap-2">
                        <SkeletonText size="sm" width="96px" />
                        <Skeleton className="h-8 w-[70px] rounded-md" />
                    </div>
                    <SkeletonText size="sm" width="120px" />
                    <div className="flex items-center gap-1">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} className="size-8 rounded-md" />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export type { SkeletonCellVariant, SkeletonColumn };
